import React, { useCallback, useState } from 'react';
import { message } from 'antd';

/**
 * 全局拖拽区域组件
 * 让整个邮件编辑器都支持文件拖拽上传
 */
const GlobalDropZone = ({ 
    children, 
    onFileDrop,
    accept = ".pdf,.xlsx,.xls,.docx,.doc,.txt,.jpg,.jpeg,.png,.gif,.zip",
    maxSize = 100 * 1024 * 1024, // 100MB
    disabled = false
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [dragCounter, setDragCounter] = useState(0);

    /**
     * 验证文件类型
     */
    const isValidFileType = useCallback((file) => {
        const allowedTypes = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
            'application/vnd.ms-excel', // xls
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
            'application/msword', // doc
            'text/plain',
            'image/jpeg',
            'image/png',
            'image/gif',
            'application/zip',
            'application/x-zip-compressed'
        ];

        return allowedTypes.includes(file.type);
    }, []);

    /**
     * 验证文件大小
     */
    const isValidFileSize = useCallback((file) => {
        return file.size <= maxSize;
    }, [maxSize]);

    /**
     * 处理拖拽进入
     */
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (disabled) return;

        setDragCounter(prev => prev + 1);
        
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragOver(true);
        }
    }, [disabled]);

    /**
     * 处理拖拽离开
     */
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (disabled) return;

        setDragCounter(prev => {
            const newCounter = prev - 1;
            if (newCounter === 0) {
                setIsDragOver(false);
            }
            return newCounter;
        });
    }, [disabled]);

    /**
     * 处理拖拽悬停
     */
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (disabled) return;

        // 设置拖拽效果
        e.dataTransfer.dropEffect = 'copy';
    }, [disabled]);

    /**
     * 处理文件放置
     */
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (disabled) return;

        setIsDragOver(false);
        setDragCounter(0);

        const files = Array.from(e.dataTransfer.files);
        
        if (files.length === 0) return;

        // 验证文件
        const validFiles = [];
        const invalidFiles = [];

        files.forEach(file => {
            if (!isValidFileType(file)) {
                invalidFiles.push({ file, reason: '不支持的文件类型' });
            } else if (!isValidFileSize(file)) {
                const maxSizeMB = Math.round(maxSize / 1024 / 1024);
                invalidFiles.push({ file, reason: `文件大小超过 ${maxSizeMB}MB` });
            } else {
                validFiles.push(file);
            }
        });

        // 显示错误信息
        if (invalidFiles.length > 0) {
            invalidFiles.forEach(({ file, reason }) => {
                message.error(`文件 "${file.name}" ${reason}`);
            });
        }

        // 处理有效文件
        if (validFiles.length > 0 && onFileDrop) {
            onFileDrop(validFiles);
        }
    }, [disabled, isValidFileType, isValidFileSize, maxSize, onFileDrop]);

    return (
        <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%'
            }}
        >
            {/* 拖拽遮罩层 */}
            {isDragOver && !disabled && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(24, 144, 255, 0.1)',
                        border: '2px dashed #1890ff',
                        borderRadius: '8px',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#1890ff',
                        pointerEvents: 'none'
                    }}
                >
                    拖拽文件到此处上传
                </div>
            )}
            
            {children}
        </div>
    );
};

export default GlobalDropZone; 